import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { GridFilterPanel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { formatDateForApi, DateFilterInput } from '../../common/DateFilterInput.js';
import { COUNTY_CODES } from '../../../common/mappings.js';

const COUNTY_SINGLE_SELECT_OPTIONS = Object.entries(COUNTY_CODES)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const SERVER_CUSTOM_KIND = { algo: 'algo', county: 'county', dateRange: 'dateRange', trialDecision: 'trialDecision', townPortal: 'townPortal' };

/** Disagree → AlgoDisagree === 1; Agree → AlgoDisagree === 0 on the server. */
export const ALGO_VARIANT = { disagree: 'disagree', agree: 'agree' };

export function newNegotiatorAnalysisCustomFilterRow() {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `scf-${Date.now()}-${Math.random()}`;
  return {
    id,
    kind: SERVER_CUSTOM_KIND.county,
    countyCode: '',
    algoVariant: '',
    dateStart: null,
    dateEnd: null,
  };
}

/** True when custom filters differ from a single default county row with no value set. */
export function negotiatorAnalysisCustomFiltersNeedToolbarClear(rows) {
  if (rows.length > 1) return true;
  if (rows.length === 0) return false;
  const r = rows[0];
  return r.kind !== SERVER_CUSTOM_KIND.county
    || Boolean(r.countyCode)
    || Boolean(r.algoVariant)
    || r.dateStart != null
    || r.dateEnd != null;
}

const DATE_PICKER_STANDARD = { textField: { variant: 'standard' } };

const negotiatorAnalysisValueSelectFormSx = {
  flex: '1 1 0',
  minWidth: 0,
  width: '100%',
  height: 48,
  minHeight: 48,
  maxHeight: 48,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  '& .MuiInputLabel-root': { pointerEvents: 'none' },
};

/** Compact row for the kind "Filter" select only; label must not intercept clicks. */
function negotiatorAnalysisKindFilterFormSx(layout) {
  return {
    position: 'relative',
    height: 48,
    maxHeight: 48,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    ...layout,
    '& .MuiInputLabel-root': {
      position: 'absolute',
      left: 0,
      top: 0,
      transformOrigin: 'top left',
      pointerEvents: 'none',
      fontSize: '0.75rem',
      lineHeight: 1.2,
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      transform: 'translate(0, 2px) scale(0.75)',
    },
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      transform: 'translate(0, 22px) scale(1)',
    },
    '& .MuiInputBase-root': {
      marginTop: '14px',
      height: 30,
      minHeight: 30,
      maxHeight: 30,
      boxSizing: 'border-box',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 0,
      paddingTop: 4,
      paddingBottom: 4,
    },
  };
}

/**
 * Query payload for /negotiator_results/all. algorithmAgreement is 'disagree' | 'agree' | null
 * (last matching algo row wins when multiple are present).
 * trialDecision is true when any Trial Decision row is present.
 * townPortal is true when any Town Portal row is present.
 */
export function deriveNegotiatorAnalysisCustomQuery(rows) {
  let algorithmAgreement = null;
  let trialDecision = false;
  let townPortal = false;
  const countyCodes = [];
  let dateLow = null;
  let dateHigh = null;
  for (const r of rows) {
    if (r.kind === SERVER_CUSTOM_KIND.trialDecision) trialDecision = true;
    if (r.kind === SERVER_CUSTOM_KIND.townPortal) townPortal = true;
    if (r.kind === SERVER_CUSTOM_KIND.algo) {
      if (r.algoVariant === ALGO_VARIANT.disagree) algorithmAgreement = 'disagree';
      else if (r.algoVariant === ALGO_VARIANT.agree) algorithmAgreement = 'agree';
    }
    if (r.kind === SERVER_CUSTOM_KIND.county && r.countyCode && !countyCodes.includes(r.countyCode)) {
      countyCodes.push(r.countyCode);
    }
    if (r.kind === SERVER_CUSTOM_KIND.dateRange) {
      const s = r.dateStart != null && dayjs(r.dateStart).isValid() ? dayjs(r.dateStart).startOf('day') : null;
      const e = r.dateEnd != null && dayjs(r.dateEnd).isValid() ? dayjs(r.dateEnd).startOf('day') : null;
      if (!s && !e) continue;
      if (s) dateLow = dateLow == null ? s : (s.isAfter(dateLow) ? s : dateLow);
      if (e) dateHigh = dateHigh == null ? e : (e.isBefore(dateHigh) ? e : dateHigh);
    }
  }
  if (dateLow && dateHigh && dateLow.isAfter(dateHigh)) {
    dateLow = null;
    dateHigh = null;
  }
  countyCodes.sort((a, b) => a.localeCompare(b));
  return {
    algorithmAgreement,
    trialDecision,
    townPortal,
    countyCodes,
    startingDateFilter: formatDateForApi(dateLow),
    endingDateFilter: formatDateForApi(dateHigh),
  };
}

export const NegotiatorAnalysisCustomFiltersPanel = React.forwardRef(function NegotiatorAnalysisCustomFiltersPanel(inProps, ref) {
  const {
    analysisCustomFilters,
    onAddAnalysisCustomFilter,
    onUpdateAnalysisCustomFilter,
    onRemoveAnalysisCustomFilter,
    onRemoveAllAnalysisCustomFilters,
    ...filterPanelProps
  } = inProps;

  const patchKindChange = (row, kind) => {
    if (kind === SERVER_CUSTOM_KIND.algo) {
      return { kind, countyCode: '', dateStart: null, dateEnd: null, algoVariant: '' };
    }
    if (kind === SERVER_CUSTOM_KIND.county) {
      return { kind, dateStart: null, dateEnd: null, algoVariant: '', countyCode: row.countyCode ?? '' };
    }
    if (kind === SERVER_CUSTOM_KIND.dateRange || kind === SERVER_CUSTOM_KIND.trialDecision || kind === SERVER_CUSTOM_KIND.townPortal) {
      return { kind, countyCode: '', dateStart: null, dateEnd: null, algoVariant: '' };
    }
    return { kind };
  };

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(85vh, 720px)',
        width: 640,
        maxWidth: '100vw',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', borderBottom: 1, borderColor: 'divider' }}>
        <GridFilterPanel {...filterPanelProps} />
      </Box>
      <Box sx={{ px: 1, py: 1.5, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}>
          Negotiator analysis custom filters
        </Typography>
        <Stack spacing={1.5}>
          <Stack spacing={1}>
            {analysisCustomFilters.map((row) => {
              const showDateNote = row.kind === SERVER_CUSTOM_KIND.dateRange;
              const showTrialNote = row.kind === SERVER_CUSTOM_KIND.trialDecision;
              const showTownPortalNote = row.kind === SERVER_CUSTOM_KIND.townPortal;
              return (
                <Box
                  key={row.id}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto minmax(0, 1fr)',
                      columnGap: 1,
                      width: '100%',
                      minWidth: 0,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        gridColumn: 1,
                        gridRow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="Remove custom filter"
                        onClick={() => onRemoveAnalysisCustomFilter(row.id)}
                        sx={{
                          flexShrink: 0,
                          color: 'grey.700',
                          p: 0,
                          minWidth: 0,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18, display: 'block' }} />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: 2,
                        gridRow: 1,
                        minWidth: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                          gap: 1,
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <FormControl
                          variant="standard"
                          size="small"
                          margin="none"
                          sx={negotiatorAnalysisKindFilterFormSx({
                            flex: '0 1 200px',
                            minWidth: 160,
                            maxWidth: 220,
                            width: '100%',
                          })}
                        >
                          <InputLabel id={`neg-cf-kind-${row.id}`}>Filter</InputLabel>
                          <Select
                            variant="standard"
                            labelId={`neg-cf-kind-${row.id}`}
                            label="Filter"
                            value={row.kind}
                            onChange={(e) => onUpdateAnalysisCustomFilter(row.id, patchKindChange(row, e.target.value))}
                          >
                            <MenuItem value={SERVER_CUSTOM_KIND.algo}>Algorithm agreement</MenuItem>
                            <MenuItem value={SERVER_CUSTOM_KIND.county}>County</MenuItem>
                            <MenuItem value={SERVER_CUSTOM_KIND.dateRange}>Date Range</MenuItem>
                            <MenuItem value={SERVER_CUSTOM_KIND.trialDecision}>Trial Decision</MenuItem>
                            <MenuItem value={SERVER_CUSTOM_KIND.townPortal}>Town Portal</MenuItem>
                          </Select>
                        </FormControl>
                        {row.kind === SERVER_CUSTOM_KIND.algo && (
                          <FormControl
                            variant="standard"
                            size="small"
                            margin="none"
                            sx={negotiatorAnalysisValueSelectFormSx}
                          >
                            <InputLabel id={`neg-cf-algo-${row.id}`}>Agreement</InputLabel>
                            <Select
                              variant="standard"
                              labelId={`neg-cf-algo-${row.id}`}
                              label="Agreement"
                              value={row.algoVariant || ''}
                              onChange={(e) => onUpdateAnalysisCustomFilter(row.id, { algoVariant: e.target.value })}
                            >
                              <MenuItem value="">Select Disagree or Agree</MenuItem>
                              <MenuItem value={ALGO_VARIANT.disagree}>Disagree</MenuItem>
                              <MenuItem value={ALGO_VARIANT.agree}>Agree</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {row.kind === SERVER_CUSTOM_KIND.county && (
                          <FormControl
                            variant="standard"
                            size="small"
                            margin="none"
                            sx={negotiatorAnalysisValueSelectFormSx}
                          >
                            <InputLabel id={`neg-cf-county-${row.id}`}>County</InputLabel>
                            <Select
                              variant="standard"
                              labelId={`neg-cf-county-${row.id}`}
                              label="County"
                              value={row.countyCode || ''}
                              onChange={(e) => onUpdateAnalysisCustomFilter(row.id, { countyCode: e.target.value })}
                            >
                              <MenuItem value="">Select county</MenuItem>
                              {COUNTY_SINGLE_SELECT_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {row.kind === SERVER_CUSTOM_KIND.dateRange && (
                          <Box
                            sx={{
                              flex: '1 1 0',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              gap: 1,
                              alignItems: 'flex-start',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <DateFilterInput
                              label="Start"
                              value={row.dateStart}
                              onChange={(d) => onUpdateAnalysisCustomFilter(row.id, { dateStart: d })}
                              slotProps={DATE_PICKER_STANDARD}
                              sx={{ flex: 1, minWidth: 120, width: 'auto', maxWidth: 'none' }}
                            />
                            <DateFilterInput
                              label="End"
                              value={row.dateEnd}
                              onChange={(d) => onUpdateAnalysisCustomFilter(row.id, { dateEnd: d })}
                              slotProps={DATE_PICKER_STANDARD}
                              sx={{ flex: 1, minWidth: 120, width: 'auto', maxWidth: 'none' }}
                            />
                          </Box>
                        )}
                      </Box>
                      {showDateNote && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.3, display: 'block', minWidth: 0 }}
                        >
                          * This is based on the Determination Date
                        </Typography>
                      )}
                      {showTrialNote && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.3, display: 'block', minWidth: 0 }}
                        >
                          Decision wins and losses only. Settlements, unresolved, and awaiting-decision rows are excluded.
                        </Typography>
                      )}
                      {showTownPortalNote && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.3, display: 'block', minWidth: 0 }}
                        >
                          Cases with any Town negotiation this year whose OfferSource is Town Portal.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
            }}
          >
            <Button
              size="small"
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onAddAnalysisCustomFilter}
              sx={{ px: 0.75, textTransform: 'none' }}
            >
              Add custom filter
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={onRemoveAllAnalysisCustomFilters}
              disabled={!negotiatorAnalysisCustomFiltersNeedToolbarClear(analysisCustomFilters)}
              sx={{ px: 0.75, textTransform: 'none' }}
            >
              Remove all custom
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
});
